/*
 * CGL online quantity limit (Phase 5, Adam's 10+ rule, 30 Oct 2026): a product with the metafield
 * cgl.max_online_qty (Mini Canvas: 9) can be bought online up to that many units per order, counting every
 * cart line of the product. One more than that goes to the bulk quote instead of checkout.
 *
 * Product page: <cgl-quantity-limit> (snippets/cgl-quantity-limit.liquid) sits by the quantity selector. It
 * adds the quantity asked for to the units already in the cart, shows the bulk message with a link that
 * carries the product and quantity, and stops Add to cart while the total is over the limit. Editing a cart
 * line never changes its quantity, so the edit page is never blocked.
 *
 * Cart page: the cart section renders the over-limit message and a disabled checkout itself (Liquid). This
 * script also stops any checkout submit while a rendered limit says the cart is over, and shows the cart's
 * bulk message when a quantity typed in the cart would go over.
 *
 * Elsewhere: a checkout button outside the cart page (the cart notification) reads the cart first and sends an
 * over-limit cart to the cart page instead of checkout.
 *
 * This is the storefront layer only. Without a server-side cart and checkout validation (not used: Adam, 31 Oct)
 * a cart link or a direct /checkout still reaches Shopify's checkout; see docs/phase-5-followup.md.
 *
 * Loaded from layout/theme.liquid before cgl-personalisation.js, so its capture listeners run first. It never
 * removes or changes a cart line.
 */
(function () {
  'use strict';

  function fill(text, values) {
    return String(text || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
    });
  }

  function withQty(href, qty) {
    try {
      var url = new URL(href, window.location.origin);
      url.searchParams.set('qty', String(qty));
      return url.origin === window.location.origin ? url.pathname + url.search : url.href;
    } catch (e) {
      return href;
    }
  }

  var editing = /[?&]cgl_edit=/.test(window.location.search);

  class CglQuantityLimit extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.max = parseInt(this.dataset.max, 10) || 0;
      this.inCart = parseInt(this.dataset.inCart, 10) || 0;
      this.productId = String(this.dataset.productId || '');
      this.alert = this.querySelector('[data-cgl-limit-alert]');
      this.message = this.querySelector('[data-cgl-limit-message]');
      this.link = this.querySelector('[data-cgl-limit-link]');
      this.onChange = this.update.bind(this);
      document.addEventListener('input', this.onChange);
      document.addEventListener('change', this.onChange);
      document.addEventListener('click', (event) => {
        if (event.target.closest && event.target.closest('quantity-input')) window.setTimeout(this.onChange, 0);
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCart());
      }
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('input', this.onChange);
      document.removeEventListener('change', this.onChange);
      if (this.unsubscribe) this.unsubscribe();
    }

    input() {
      return document.getElementById(this.dataset.input);
    }

    wanted() {
      var input = this.input();
      var value = input ? parseInt(input.value, 10) : 1;
      return isNaN(value) || value < 1 ? 1 : value;
    }

    total() {
      return this.wanted() + this.inCart;
    }

    over() {
      return this.max > 0 && !editing && this.total() > this.max;
    }

    refreshCart() {
      fetch(window.routes && window.routes.cart_url ? window.routes.cart_url + '.js' : '/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          if (!cart || !Array.isArray(cart.items)) return;
          var id = this.productId;
          this.inCart = cart.items.reduce(function (sum, item) {
            return String(item.product_id) === id ? sum + item.quantity : sum;
          }, 0);
          this.update();
        })
        .catch(function () {});
    }

    update() {
      var over = this.over();
      this.toggleAttribute('data-over', over);
      var form = document.getElementById(this.dataset.form);
      var button = form ? form.querySelector('[type="submit"][name="add"]') : null;
      if (button) {
        // Over the limit Add to cart reads as unavailable (aria-disabled, dimmed) but stays focusable, so a click
        // or Enter still lands on the explanation and the bulk quote link instead of doing nothing silently.
        if (over) {
          button.setAttribute('aria-describedby', this.alert.id);
          button.setAttribute('aria-disabled', 'true');
          button.dataset.cglLimit = 'true';
        } else if (button.dataset.cglLimit) {
          button.removeAttribute('aria-describedby');
          button.removeAttribute('aria-disabled');
          delete button.dataset.cglLimit;
        }
      }
      if (!this.alert) return;
      this.alert.hidden = !over;
      if (!over) return;
      var total = this.total();
      var template = this.inCart > 0 ? this.dataset.textInCart : this.dataset.text;
      this.message.textContent = fill(template, { max: this.max, count: total, in_cart: this.inCart, bulk: this.max + 1 });
      if (this.link) this.link.setAttribute('href', withQty(this.link.getAttribute('href'), total));
    }

    block() {
      this.dataset.blocked = String((parseInt(this.dataset.blocked, 10) || 0) + 1);
      this.update();
      if (!this.alert) return;
      this.alert.hidden = false;
      this.alert.setAttribute('tabindex', '-1');
      this.alert.focus({ preventScroll: true });
      this.alert.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  if (!customElements.get('cgl-quantity-limit')) customElements.define('cgl-quantity-limit', CglQuantityLimit);

  // The product form has an input named "id" (the variant), so form.id is that input, not the form's id.
  function limitFor(form) {
    var id = form && form.getAttribute ? form.getAttribute('id') : null;
    if (!id) return null;
    var found = null;
    document.querySelectorAll('cgl-quantity-limit').forEach(function (el) {
      if (el.dataset.form === id && el.isConnected) found = el;
    });
    return found;
  }

  function cartOver() {
    return !!document.querySelector('[data-cgl-cart-limit][data-over="true"]');
  }

  function isCheckout(event) {
    var submitter = event.submitter;
    var form = event.target;
    if (submitter && submitter.name === 'checkout') return true;
    return !!(form && form.getAttribute && /\/checkout/.test(form.getAttribute('action') || ''));
  }

  function showCartLimit() {
    var box = document.querySelector('[data-cgl-cart-limit]');
    if (!box) return;
    box.hidden = false;
    box.setAttribute('tabindex', '-1');
    box.focus({ preventScroll: true });
    box.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  // Capture phase on window: before Ride's product form and before the CGL personalisation block.
  window.addEventListener(
    'submit',
    function (event) {
      var limit = limitFor(event.target);
      if (limit && limit.over()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        limit.block();
        return;
      }
      if (isCheckout(event) && cartOver()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showCartLimit();
      }
    },
    true
  );

  window.addEventListener(
    'click',
    function (event) {
      var button = event.target && event.target.closest ? event.target.closest('button[type="submit"], input[type="submit"]') : null;
      if (!button) return;
      var limit = limitFor(button.form);
      if (limit && limit.over()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        limit.block();
        return;
      }
      if (button.name === 'checkout' && cartOver()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showCartLimit();
      }
    },
    true
  );

  // Checkout buttons the cart page doesn't render (the cart notification after an add or a personalisation edit)
  // can't see the rendered limit, so the cart is read first: over a product's limit, the shopper goes to the cart
  // page, which explains it and offers the bulk quote; otherwise the checkout carries on. Limits come from
  // snippets/cgl-limits-data.liquid.
  function limitsData() {
    var el = document.getElementById('cgl-limits-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function cartOverLimits(cart, limits) {
    var totals = {};
    (cart.items || []).forEach(function (item) {
      var id = String(item.product_id);
      totals[id] = (totals[id] || 0) + item.quantity;
    });
    return Object.keys(limits).some(function (id) {
      return totals[id] > limits[id];
    });
  }

  function cartUrl() {
    return window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
  }

  window.addEventListener(
    'click',
    function (event) {
      var button = event.target && event.target.closest ? event.target.closest('button[name="checkout"]') : null;
      if (!button || !button.form || document.getElementById('main-cart-items')) return;
      if (button.dataset.cglChecked === 'true') {
        delete button.dataset.cglChecked;
        return;
      }
      var limits = limitsData();
      if (!limits || !Object.keys(limits).length) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (cart) {
          if (cart && cartOverLimits(cart, limits)) {
            window.location.href = cartUrl();
            return;
          }
          button.dataset.cglChecked = 'true';
          button.click();
        })
        .catch(function () {
          window.location.href = cartUrl();
        });
    },
    true
  );

  // Cart page: Ride re-renders the line items after each quantity change but not the checkout buttons, so their
  // state follows the rendered limit here. Only buttons this script or the cart section turned off are turned on.
  function syncCheckout() {
    var over = cartOver();
    document.querySelectorAll('button[name="checkout"]').forEach(function (button) {
      if (over) {
        button.disabled = true;
        button.setAttribute('data-cgl-limit-disabled', '');
      } else if (button.hasAttribute('data-cgl-limit-disabled')) {
        button.disabled = false;
        button.removeAttribute('data-cgl-limit-disabled');
      }
    });
    document.querySelectorAll('[data-cgl-express]').forEach(function (box) {
      box.hidden = over;
    });
  }

  function watchCart() {
    var items = document.getElementById('main-cart-items');
    if (items && 'MutationObserver' in window) new MutationObserver(syncCheckout).observe(items, { childList: true, subtree: true });
    syncCheckout();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCart);
  else watchCart();

  // Cart page: a quantity typed above a line's limit is refused by Ride's own check (the input's max); this
  // also shows the cart's bulk message, with the quantity asked for, so the shopper sees where 10+ goes.
  window.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('[data-cgl-limit-max]')) return;
      var value = parseInt(input.value, 10);
      var max = parseInt(input.dataset.cglLimitMax, 10);
      if (isNaN(value) || isNaN(max) || value <= max) return;
      var box = document.querySelector('[data-cgl-cart-limit-hint="' + input.dataset.cglLimitProduct + '"]');
      if (!box) return;
      var others = parseInt(input.dataset.cglLimitOthers, 10) || 0;
      var link = box.querySelector('[data-cgl-limit-link]');
      if (link) link.setAttribute('href', withQty(link.getAttribute('href'), value + others));
      box.hidden = false;
    },
    true
  );
})();
